import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaNewspaper,
  FaSearch,
} from 'react-icons/fa';
import {
  MdPolicy,
  MdSchool,
  MdEvent,
  MdScience,
  MdCampaign,
} from 'react-icons/md';
import { FiRss } from 'react-icons/fi';
import { ALL_POSTS, CATEGORIES, getCategoryLabel, hasArticle } from '../../data/posts'; 
import './Blog.css';

const CATEGORY_ICONS = {
  policy:    <MdPolicy    size={32} style={{ color: '#fff' }} />,
  education: <MdSchool    size={32} style={{ color: '#fff' }} />,
  events:    <MdEvent     size={32} style={{ color: '#fff' }} />,
  research:  <MdScience   size={32} style={{ color: '#fff' }} />,
  news:      <MdCampaign  size={32} style={{ color: '#fff' }} />,
};

const comingSoon = () => alert('Full articles coming soon!');

/* ── Featured Post Component ── */
function FeaturedPost({ post }) {
  const { category, date, title, excerpt, author, readTime, slug } = post;

  return (
    <div className="blog-featured">
      <article className="blog-featured__card">
        <div className={`blog-featured__image blog-card__image--${category}`}>
          <div
            className="blog-featured__image-icon"
            aria-hidden="true"
          >
            <FaNewspaper size={180} style={{ color: '#fff' }} />
          </div>
          <div className="blog-featured__image-label">
            <div
              className="blog-featured__image-text"
              aria-hidden="true"
            >
              {title}
            </div>
          </div>
        </div>

        <div className="blog-featured__body">
          <div className="blog-featured__meta">
            <span className="blog-featured__badge">Featured</span>
            <span className="blog-featured__tag">
              {getCategoryLabel(category)}
            </span>
            <time className="blog-featured__date">{date}</time>
            <span className="blog-featured__date">{readTime}</span>
          </div>

          <h2 className="blog-featured__title">{title}</h2>
          <p className="blog-featured__excerpt">{excerpt}</p>

          <div className="blog-featured__author">
            <div
              className="blog-featured__author-avatar"
              aria-hidden="true"
            >
              {author.initials}
            </div>
            <div>
              <div className="blog-featured__author-name">
                {author.name}
              </div>
              <div className="blog-featured__author-role">
                {author.role}
              </div>
            </div>
          </div>

          {hasArticle(post) ? (
            <Link
              to={`/blog/${slug}`}
              className="blog-featured__read-more"
              aria-label={`Read full article: ${title}`}
            >
              Read Full Article
              <FaArrowRight size={13} aria-hidden="true" />
            </Link>
          ) : (
            <button
              className="blog-featured__read-more"
              onClick={comingSoon}
              aria-label="Read full article - coming soon"
            >
              Read Full Article
              <FaArrowRight size={13} aria-hidden="true" />
            </button>
          )}
        </div>
      </article>
    </div>
  );
}

/* ── Blog Card Component ── */
function BlogCard({ post }) {
  const { category, date, title, excerpt, author, readTime, slug } = post;

  return (
    <article className="blog-card">
      <div className={`blog-card__image blog-card__image--${category}`}>
        <div className="blog-card__image-icon" aria-hidden="true">
          {CATEGORY_ICONS[category]}
        </div>
      </div>

      <div className="blog-card__body">
        <div className="blog-card__meta">
          <span className="blog-card__tag">
            {getCategoryLabel(category)}
          </span>
          <time className="blog-card__date">{date}</time>
        </div>

        <h3 className="blog-card__title">{title}</h3>
        <p className="blog-card__excerpt">{excerpt}</p>

        <div className="blog-card__footer">
          <div className="blog-card__author">
            <div
              className="blog-card__author-avatar"
              aria-hidden="true"
            >
              {author.initials}
            </div>
            <span className="blog-card__author-name">{author.name}</span>
          </div>

          {hasArticle(post) ? (
            <Link
              to={`/blog/${slug}`}
              className="blog-card__read-more"
              aria-label={`Read full article: ${title}`}
            >
              {readTime}
              <FaArrowRight size={11} aria-hidden="true" />
            </Link>
          ) : (
            <button
              className="blog-card__read-more"
              onClick={comingSoon}
              aria-label="Read full article - coming soon"
            >
              {readTime}
              <FaArrowRight size={11} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

/* BLOG PAGE */
function Blog() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [email, setEmail]                   = useState('');
  const [subscribed, setSubscribed]         = useState(false);

  const featuredPost = ALL_POSTS.find(p => p.featured);

  const filteredPosts = useMemo(() => {
    const nonFeatured = ALL_POSTS.filter(p => !p.featured);
    if (activeCategory === 'all') return nonFeatured;
    return nonFeatured.filter(p => p.category === activeCategory);
  }, [activeCategory]);

  function handleSubscribe(e) {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  }

  return (
    <>
      {/* ── Page Hero ── */}
      <section
        className="blog-hero"
        aria-labelledby="blog-hero-heading"
      >
        <div className="blog-hero__inner">
          <div aria-hidden="true">
            <FiRss size={44} style={{ color: '#C9A84C' }} />
          </div>
          <h1
            className="blog-hero__title"
            id="blog-hero-heading"
          >
            NSK News & Insights
          </h1>
          <p className="blog-hero__subtitle">
            Articles, research updates, policy analysis, and event coverage
            from Kenya's nuclear science community.
          </p>
        </div>
      </section>

      {/* ── Filter Bar ── */}
      <nav
        className="blog-filters"
        aria-label="Filter blog posts by category"
      >
        <div className="blog-filters__inner">
          <span className="blog-filters__label" id="filter-label">
            Filter:
          </span>
          {CATEGORIES.map(({ id, label }) => (
            <button
              key={id}
              className={`blog-filter-btn ${activeCategory === id ? 'active' : ''}`}
              onClick={() => setActiveCategory(id)}
              aria-pressed={activeCategory === id}
              aria-label={`Filter by ${label}`}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Blog Content ── */}
      <section
        className="blog-content"
        aria-labelledby="blog-content-heading"
      >
        <div className="blog-content__inner">

          {/* Featured post — only show when "All" is selected */}
          {activeCategory === 'all' && featuredPost && (
            <FeaturedPost post={featuredPost} />
          )}

          {/* Grid header */}
          <div className="blog-grid__header">
            <h2 className="blog-grid__title" id="blog-content-heading">
              {activeCategory === 'all'
                ? 'Latest Articles'
                : `${getCategoryLabel(activeCategory)} Articles`}
            </h2>
            <span className="blog-grid__count">
              {filteredPosts.length}{' '}
              {filteredPosts.length === 1 ? 'article' : 'articles'}
            </span>
          </div>

          {/* Posts grid */}
          <div className="blog-grid" role="list">
            {filteredPosts.length > 0 ? (
              filteredPosts.map(post => (
                <div role="listitem" key={post.id}>
                  <BlogCard post={post} />
                </div>
              ))
            ) : (
              <div className="blog-empty">
                <div className="blog-empty__icon" aria-hidden="true">
                  <FaSearch
                    size={40}
                    style={{ color: 'var(--color-grey-mid)' }}
                  />
                </div>
                <h3 className="blog-empty__title">No articles found</h3>
                <p className="blog-empty__text">
                  No posts in this category yet. Check back soon.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Newsletter Banner ── */}
      <section
        className="blog-newsletter"
        aria-labelledby="newsletter-heading"
      >
        <div className="blog-newsletter__inner">
          <div aria-hidden="true">
            <FiRss size={40} style={{ color: '#C9A84C' }} />
          </div>
          <h2
            className="blog-newsletter__title"
            id="newsletter-heading"
          >
            Stay <span>Informed</span>
          </h2>
          <p className="blog-newsletter__text">
            Subscribe to the NSK newsletter and get the latest nuclear
            science news, policy updates, and event announcements
            delivered to your inbox.
          </p>

          {subscribed ? (
            <p style={{
              color: '#C9A84C',
              fontWeight: 600,
              fontSize: 'var(--text-lg)',
            }}>
              ✓ Thank you for subscribing!
            </p>
          ) : (
            <form
              className="blog-newsletter__form"
              onSubmit={handleSubscribe}
              aria-label="Newsletter subscription form"
            >
              <label htmlFor="newsletter-email" className="sr-only">
                Your email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                className="blog-newsletter__input"
                placeholder="Your email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                aria-required="true"
                autoComplete="email"
              />
              <button
                type="submit"
                className="btn btn--primary"
              >
                Subscribe
              </button>
            </form>
          )}

          <p className="blog-newsletter__note">
            No spam. Unsubscribe at any time.
          </p>
        </div>
      </section>
    </>
  );
}

export default Blog;